import { Box, useTheme } from '@mui/material';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridColumns, GridRows } from '@visx/grid';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    axisStyle,
    chartPaletteFromTheme,
} from '~/components/Charts/chartStyle';
import { linePathD } from '~/components/Charts/linePathD';
import { plotAreaWithin } from '~/components/Charts/plotArea';
import { INTENSITY_DISPLAY_DECIMALS } from '~/domain/dicom/intensityUnits';
import LineProfileHoverLayer from './LineProfileHoverLayer';
import { ProfileCurve } from './lineProfileCurves';

const FONTS = { tick: 10, label: 11 };
const MARGIN = { top: 12, right: 16, left: 58 };
/** Space under the plot with and without the distance axis, in pixels. */
const BOTTOM_MARGIN = { withAxis: 44, withoutAxis: 10 };
/** Plot width per distance label, so labels stay apart on a narrow chart. */
const PX_PER_X_TICK = 56;

/**
 * The intensity-vs-distance chart at a known width: x in cm, y in the
 * display unit, one path per curve, broken wherever a curve has a gap.
 */
export function SizedLineProfileChart({
    width,
    curves,
    unit,
    height,
    xMaxCm,
    showDistanceAxis,
    onHoverDistance,
}: {
    width: number;
    curves: ProfileCurve[];
    unit: string;
    height: number;
    xMaxCm: number | undefined;
    showDistanceAxis: boolean;
    onHoverDistance?: (distanceCm: number | undefined) => void;
}) {
    const theme = useTheme();
    const intl = useIntl();
    const palette = useMemo(() => chartPaletteFromTheme(theme), [theme]);
    const data = useMemo(
        () => curves.filter((curve) => curve.data.length > 0),
        [curves]
    );
    const plot = plotAreaWithin(width, height, {
        ...MARGIN,
        bottom: showDistanceAxis
            ? BOTTOM_MARGIN.withAxis
            : BOTTOM_MARGIN.withoutAxis,
    });
    // ~36px of plot height per label keeps y labels from colliding.
    const yTickCount = Math.max(3, Math.floor(height / 36));
    const { xScale, yScale } = useMemo(() => {
        let xMax = 0;
        let yMin = Infinity;
        let yMax = -Infinity;
        for (const curve of data) {
            for (const point of curve.data) {
                xMax = point.x > xMax ? point.x : xMax;
                if (point.y !== null) {
                    yMin = point.y < yMin ? point.y : yMin;
                    yMax = point.y > yMax ? point.y : yMax;
                }
            }
        }
        if (yMin === Infinity) {
            yMin = 0;
            yMax = 1;
        }
        return {
            xScale: scaleLinear<number>({
                domain: [0, xMaxCm ?? xMax],
                range: [0, plot.width],
            }),
            yScale: scaleLinear<number>({
                domain: [yMin, yMax],
                range: [plot.height, 0],
                nice: true,
            }),
        };
    }, [data, xMaxCm, plot.width, plot.height]);
    const yTicks = yScale.ticks(yTickCount);
    const xTicks = xScale.ticks(
        Math.max(2, Math.floor(plot.width / PX_PER_X_TICK))
    );

    return (
        <svg width={width} height={height} data-testid="line-profile-chart">
            <g transform={`translate(${plot.left}, ${plot.top})`}>
                <GridRows
                    scale={yScale}
                    width={plot.width}
                    tickValues={yTicks}
                    stroke={palette.grid}
                />
                <GridColumns
                    scale={xScale}
                    height={plot.height}
                    tickValues={xTicks}
                    stroke={palette.grid}
                />
                {data.map((curve) => (
                    <path
                        key={curve.id}
                        d={linePathD(
                            curve.data,
                            (point) => xScale(point.x),
                            (point) =>
                                point.y === null ? null : yScale(point.y)
                        )}
                        stroke={curve.color}
                        strokeWidth={1.75}
                        fill="none"
                        data-testid="line-profile-path"
                    />
                ))}
            </g>
            {showDistanceAxis ? (
                <AxisBottom
                    top={plot.top + plot.height}
                    left={plot.left}
                    scale={xScale}
                    tickValues={xTicks}
                    label={intl.formatMessage({
                        id: 'lineProfile.axisDistance',
                    })}
                    labelOffset={18}
                    {...axisStyle(palette, FONTS, 'bottom')}
                />
            ) : null}
            <AxisLeft
                top={plot.top}
                left={plot.left}
                scale={yScale}
                tickValues={yTicks}
                tickFormat={(value) =>
                    Number(value).toFixed(INTENSITY_DISPLAY_DECIMALS)
                }
                label={
                    unit ||
                    intl.formatMessage({ id: 'lineProfile.axisIntensity' })
                }
                labelOffset={40}
                {...axisStyle(palette, FONTS, 'left')}
            />
            <LineProfileHoverLayer
                curves={data}
                xScale={xScale}
                yScale={yScale}
                plot={plot}
                unit={unit}
                palette={palette}
                onHoverDistance={onHoverDistance}
            />
        </svg>
    );
}

/**
 * Intensity-vs-distance chart for curves sharing one display unit (x in cm,
 * y in the unit), theme-aware, filling its container's width. Stacked unit
 * charts form one figure: all receive the same `xMaxCm` and only the bottom
 * one shows the distance axis. Memoized so a re-render with the same curves
 * skips chart re-layout; `onHoverDistance` must keep its identity for that.
 */
function LineProfileChart({
    curves,
    unit,
    height = 220,
    xMaxCm = undefined,
    showDistanceAxis = true,
    onHoverDistance,
}: {
    curves: ProfileCurve[];
    unit: string;
    height?: number;
    /** Shared x-axis maximum (cm) across stacked charts; auto when absent. */
    xMaxCm?: number;
    showDistanceAxis?: boolean;
    /** The snapped distance (cm) under the pointer; undefined on leave. */
    onHoverDistance?: (distanceCm: number | undefined) => void;
}) {
    return (
        <Box sx={{ width: '100%', height }}>
            <ParentSize debounceTime={0} ignoreDimensions={['height']}>
                {({ width }) =>
                    width > 0 ? (
                        <SizedLineProfileChart
                            width={width}
                            curves={curves}
                            unit={unit}
                            height={height}
                            xMaxCm={xMaxCm}
                            showDistanceAxis={showDistanceAxis}
                            onHoverDistance={onHoverDistance}
                        />
                    ) : null
                }
            </ParentSize>
        </Box>
    );
}

export default memo(LineProfileChart);
